import {
  compareCreatorTips,
  compareKeyMetricsEngagementTips,
  compareKeyMetricsImpressionsTips,
  compareKeyMetricsNegativeTips,
  comparePublicationsTips,
  compareKeyMetricsViewsTips,
  SteamAddedPositiveFilterTips,
  SteamAllReviewsFilterTips,
  SteamAllReviewsPositiveFilterTips,
  steamPerformanceFilteredReviewsTips,
  SteamRecentReviewsFilterTips,
  SteamRecentReviewsPositiveFilterTips,
  TipsMapType,
  commonBrandHealthTips,
} from '@op/components/BubbleCommon';
import metacritic from '@op/assets/svg/channel/metacritic.svg';
import opencritic from '@op/assets/svg/channel/opencritic.svg';
import steam from '@op/assets/svg/channel/steam.svg';
import xbox from '@op/assets/svg/channel/xbox.svg';
import playstation from '@op/assets/svg/channel/ps.svg';
import app_store from '@op/assets/svg/channel/app_store.svg';
import google_play from '@op/assets/svg/channel/google_play.svg';
import hatchet from '@op/assets/svg/hatchet.svg';
import google_trends from '@op/assets/imgs/icon-google.png';
import app_annie from '@op/assets/svg/app-annie.svg';
import mscience_icon from '@op/assets/svg/mscience-icon.svg';
import sensor_tower from '@op/assets/svg/sensor_tower.svg';
import { arrayToObj } from '@op/utils';
import {
  brandHealthFormat,
  percentageFormat,
  reviewsFormat,
  scoreFormat,
} from '@op/utils/value';

export type MetricsType =
  | 'Opinions Overview'
  | 'Game Store'
  | 'Community'
  | 'Operation Metrics'
  | 'PR';

/**
 * value: 指标值
 * text: 指标名称
 * tips: 提示信息
 * entity_types: 指标适用游戏平台, 默认为所有平台
 */
export type MetricsItemType = {
  value: string;
  text: string;
  tips?: TipsMapType;
  icon?: string;
  isSeparate?: boolean;
  text_zh?: string;
  text_en?: string;
  format?: (value: number) => string | number;
  width?: number;
  isPercent?: boolean;
  yAxisLimit?: any;
  noSplit?: string[];
  processType?: 'brand_health' | 1 | 100 | 5 | 10;
};

type GameStoreMetricsType = {
  [key: string]: {
    entity_types: Array<'pc' | 'console' | 'mobile'>;
    list: Array<MetricsItemType>;
  };
};

type OperationMetricsType = {
  [key in 'pc' | 'console' | 'mobile']: Array<MetricsItemType>;
};

const overview: Array<MetricsItemType> = [
  {
    text: 'Mentions',
    value: 'mentions',
    text_zh: '声量',
    format: (value) => reviewsFormat(value, 2, '0'),
  },
  {
    text: 'Brand Health',
    value: 'brand_health',
    text_zh: '品牌健康度',
    tips: commonBrandHealthTips,
    format: (value) => {
      if (value === null) {
        return '-';
      }
      return brandHealthFormat(value);
    },
    processType: 'brand_health',
  },
  { text: 'Sentiment', value: 'sentiment', text_zh: '情感分', processType: 5 },
  {
    text: 'Non Negative%',
    value: 'non_negative',
    text_zh: '非负情感占比',
    format: (value) => percentageFormat(value * 100, '-', 2),
    isPercent: true,
    yAxisLimit: {
      min: 0,
      max: 1,
    },
    processType: 1,
  },
];

const gameStore: GameStoreMetricsType = {
  google_play: {
    entity_types: ['mobile'],
    list: [
      {
        text: 'Lifetime Rating',
        value: 'google_play_lifetime_rating',
        icon: 'google_play',
        text_zh: '历史累计评分',
        format: (value: number) => scoreFormat(value, 1),
        noSplit: ['channel'],
        processType: 5,
      },
      {
        text: 'Lifetime Reviews-1',
        value: 'google_play_lifetime_reviews',
        text_zh: '历史累计评分量',
        text_en: 'Lifetime Reviews',
        noSplit: ['channel'],
      },
      {
        text: 'Lifetime Negative%',
        value: 'google_play_lifetime_negative',
        text_zh: '历史累计负面评分占比',
        noSplit: ['channel'],
        format: (value) => percentageFormat(value * 100, '-', 2),
        tips: compareKeyMetricsNegativeTips,
        isPercent: true,
        yAxisLimit: {
          min: 0,
          max: 1,
        },
        processType: 1,
      },
      {
        text: '',
        value: 'H',
        isSeparate: true,
      },
      {
        text: 'Added Rating',
        value: 'google_play_filtered_rating',
        text_zh: '新增评论平均评分',
        format: (value: number) => scoreFormat(value, 1),
        noSplit: ['channel'],
        processType: 5,
      },
      {
        text: 'Added Reviews',
        value: 'google_play_filtered_reviews',
        text_zh: '新增评论',
        format: (value) => reviewsFormat(value, 2, '0'),
        noSplit: ['channel'],
      },
      {
        text: 'Added Negative%',
        value: 'google_play_filtered_negative',
        text_zh: '新增评论负面评论占比',
        format: (value) => percentageFormat(value * 100, '-', 2),
        isPercent: true,
        yAxisLimit: {
          min: 0,
          max: 1,
        },
        noSplit: ['channel'],
        tips: compareKeyMetricsNegativeTips,
        processType: 1,
      },
    ],
  },
  app_store: {
    entity_types: ['mobile'],
    list: [
      {
        text: 'Lifetime Rating',
        value: 'app_store_lifetime_rating',
        text_zh: '历史累计评分',
        format: (value: number) => scoreFormat(value, 1),
        noSplit: ['channel', 'language'],
      },
      {
        text: 'Lifetime Reviews-1',
        value: 'app_store_lifetime_reviews',
        text_zh: '历史累计评分量',
        text_en: 'Lifetime Reviews',
        noSplit: ['channel', 'language'],
      },
      {
        text: 'Lifetime Negative%',
        value: 'app_store_lifetime_negative',
        text_zh: '历史累计负面评分占比',
        noSplit: ['channel', 'language'],
        format: (value) => percentageFormat(value * 100, '-', 2),
        tips: compareKeyMetricsNegativeTips,
        isPercent: true,
        yAxisLimit: {
          min: 0,
          max: 1,
        },
        processType: 1,
      },
      {
        text: '',
        value: 'F',
        isSeparate: true,
      },
      {
        text: 'Added Rating',
        value: 'app_store_filtered_rating',
        text_zh: '新增评论平均评分',
        format: (value: number) => scoreFormat(value, 1),
        noSplit: ['channel'],
      },
      {
        text: 'Added Reviews',
        value: 'app_store_filtered_reviews',
        text_zh: '新增评论',
        format: (value) => reviewsFormat(value, 2, '0'),
        noSplit: ['channel'],
      },
      {
        text: 'Added Negative%',
        value: 'app_store_filtered_negative',
        text_zh: '新增评论负面评论占比',
        format: (value) => percentageFormat(value * 100, '-', 2),
        isPercent: true,
        yAxisLimit: {
          min: 0,
          max: 1,
        },
        noSplit: ['channel'],
        tips: compareKeyMetricsNegativeTips,
        processType: 1,
      },
    ],
  },
  steam: {
    entity_types: ['pc'],
    list: [
      {
        text: 'All Reviews',
        value: 'steam_all_reviews',
        noSplit: ['channel', 'language', 'region', 'country'],
        tips: SteamAllReviewsFilterTips,
      },
      {
        text: 'All Reviews Positive%-1',
        value: 'steam_all_reviews_positive',
        text_zh: '全部评论正面%',
        text_en: 'All Reviews Positive%',
        format: (value) => percentageFormat(value * 100, '-', 0),
        isPercent: true,
        yAxisLimit: {
          min: 0,
          max: 1,
        },
        noSplit: ['channel', 'language', 'region', 'country'],
        tips: SteamAllReviewsPositiveFilterTips,
        processType: 1,
      },
      {
        text: '',
        value: 'E',
        isSeparate: true,
      },
      {
        text: 'Recent Reviews',
        value: 'steam_recent_reviews',
        text_zh: '近期评论',
        noSplit: ['channel', 'language', 'region', 'country'],
        tips: SteamRecentReviewsFilterTips,
      },
      {
        text: 'Recent Reviews Positive%-1',
        value: 'steam_recent_reviews_positive',
        text_zh: '近期评论正面%',
        text_en: 'Recent Reviews Positive%',
        format: (value) => percentageFormat(value * 100, '-', 0),
        noSplit: ['channel', 'language', 'region', 'country'],
        tips: SteamRecentReviewsPositiveFilterTips,
        isPercent: true,
        yAxisLimit: {
          min: 0,
          max: 1,
        },
        processType: 1,
      },
      {
        text: '',
        value: 'D',
        isSeparate: true,
      },
      {
        text: 'Added Reviews',
        value: 'steam_added_reviews',
        text_zh: '新增评论',
        format: (value) => reviewsFormat(value, 2, '0'),
        tips: steamPerformanceFilteredReviewsTips,
      },
      {
        text: 'Added Reviews Positive%',
        value: 'steam_added_positive',
        text_zh: '新增评论正面%',
        format: (value) => percentageFormat(value * 100, '-', 2),
        isPercent: true,
        yAxisLimit: {
          min: 0,
          max: 1,
        },
        tips: SteamAddedPositiveFilterTips,
        processType: 1,
      },
    ],
  },
  metacritic: {
    entity_types: ['pc', 'console'],
    list: [
      {
        text: 'Metascore',
        value: 'metacritic_metascore',
        text_zh: 'Meta评分',
        noSplit: ['channel', 'language', 'region', 'country'],
        processType: 100,
      },
      {
        text: 'User Score',
        value: 'metacritic_user_score',
        text_zh: '用户评分',
        noSplit: ['channel', 'language', 'region', 'country'],
        processType: 10,
      },
      {
        text: 'User Reviews',
        value: 'metacritic_user_reviews',
        text_zh: '使用者评论',
        noSplit: ['channel', 'language', 'region', 'country'],
      },
    ],
  },
  opencritic: {
    entity_types: ['pc', 'console'],
    list: [
      {
        text: 'Top Critic Average',
        value: 'opencritic_top_critic_average',
        text_zh: 'Critic 平均值',
        noSplit: ['channel', 'language', 'region', 'country'],
        processType: 100,
      },
      {
        text: 'Recommended%-1',
        value: 'opencritic_recommended',
        text_zh: '推荐%',
        text_en: 'Recommended%',
        format: (value) => percentageFormat(value, '-', 0),
        isPercent: true,
        yAxisLimit: {
          min: 0,
          max: 100,
        },
        noSplit: ['channel', 'language', 'region', 'country'],
        processType: 100,
      },
      {
        text: 'Critic Reviews',
        value: 'opencritic_critic_reviews',
        text_zh: 'Critic专业评论',
        noSplit: ['channel', 'language', 'region', 'country'],
      },
    ],
  },
  xbox: {
    entity_types: ['console'],
    list: [
      {
        text: 'Lifetime Rating',
        value: 'xbox_lifetime_rating',
        text_zh: '历史累计评分',
        format: (value: number) => scoreFormat(value, 1),
        noSplit: ['channel', 'language'],
        processType: 5,
      },
      {
        text: 'Lifetime Reviews-1',
        value: 'xbox_lifetime_reviews',
        text_zh: '历史累计评分量',
        text_en: 'Lifetime Reviews',
        noSplit: ['channel', 'language'],
      },
      {
        text: 'Lifetime Negative%',
        value: 'xbox_lifetime_negative',
        text_zh: '历史累计负面评分占比',
        format: (value) => percentageFormat(value * 100, '-', 2),
        isPercent: true,
        yAxisLimit: {
          min: 0,
          max: 1,
        },
        noSplit: ['channel', 'language'],
        tips: compareKeyMetricsNegativeTips,
        processType: 1,
      },
      {
        text: '',
        value: 'C',
        isSeparate: true,
      },
      {
        text: 'Filtered Rating',
        value: 'xbox_filtered_rating',
        format: (value: number) => scoreFormat(value, 1),
        noSplit: ['channel', 'language'],
        processType: 5,
      },
      {
        text: 'Filtered Reviews',
        value: 'xbox_filtered_reviews',
        noSplit: ['channel', 'language'],
      },
      {
        text: 'Filtered Negative%',
        value: 'xbox_filtered_negative',
        format: (value) => percentageFormat(value * 100, '-', 2),
        isPercent: true,
        yAxisLimit: {
          min: 0,
          max: 1,
        },
        noSplit: ['channel', 'language'],
        tips: compareKeyMetricsNegativeTips,
        processType: 1,
      },
    ],
  },
  playstation: {
    entity_types: ['console'],
    list: [
      {
        text: 'Lifetime Rating',
        value: 'playstation_lifetime_rating',
        text_zh: '历史累计评分',
        format: (value: number) => scoreFormat(value, 1),
        noSplit: ['channel', 'language'],
        processType: 5,
      },
      {
        text: 'Lifetime Reviews-1',
        value: 'playstation_lifetime_reviews',
        text_zh: '历史累计评分量',
        text_en: 'Lifetime Reviews',
        noSplit: ['channel', 'language'],
      },
      {
        text: 'Lifetime Negative%',
        value: 'playstation_lifetime_negative',
        text_zh: '历史累计负面评分占比',
        format: (value) => percentageFormat(value * 100, '-', 2),
        isPercent: true,
        yAxisLimit: {
          min: 0,
          max: 1,
        },
        noSplit: ['channel', 'language'],
        tips: compareKeyMetricsNegativeTips,
        processType: 1,
      },
    ],
  },
};

const community: Array<MetricsItemType> = [
  {
    text: 'Engagement',
    value: 'engagement',
    text_zh: '互动量',
    tips: compareKeyMetricsEngagementTips,
  },
  {
    text: 'Likes',
    value: 'likes',
    text_zh: '点赞量',
  },
  {
    text: 'Comments (Exclude Discord)',
    value: 'exclude_discord_comments',
    text_zh: '社媒评论(除Discord)',
    width: 260,
    format: (value) => reviewsFormat(value, 2, '0'),
  },
  {
    text: 'Shares',
    value: 'shares',
    text_zh: '分享量',
  },
  {
    text: '',
    value: 'B',
    isSeparate: true,
  },
  {
    text: 'Potential Impressions',
    value: 'impressions',
    text_zh: '潜在曝光',
    tips: compareKeyMetricsImpressionsTips,
  },
  {
    text: 'Publications',
    value: 'publications',
    text_zh: '发帖量',
    tips: comparePublicationsTips,
  },
  {
    text: 'Number of Creators',
    value: 'creators',
    text_zh: '创作者量',
    format: (value) => reviewsFormat(value, 2, '0'),
    tips: compareCreatorTips,
  },
  {
    text: 'Views',
    value: 'views',
    text_zh: '观看量',
    tips: compareKeyMetricsViewsTips,
  },
  // {
  //   text: '',
  //   value: 'A',
  //   isSeparate: true,
  // },
  // {
  //   text: 'Hours Watched (hours)',
  //   value: 'hours_watched',
  //   icon: 'hatchet',
  //   text_zh: '观看时长(小时)',
  //   width: 260,
  //   noSplit: ['language', 'country', 'region'],
  // },
  // {
  //   text: 'Peak CCV',
  //   value: 'peak_ccv',
  //   icon: 'hatchet',
  //   text_zh: '直播观众峰值',
  //   noSplit: ['language', 'country', 'region'],
  // },
  // {
  //   text: 'Avg CCV',
  //   value: 'avg_ccv',
  //   icon: 'hatchet',
  //   text_zh: '直播观众平均值',
  //   noSplit: ['language', 'country', 'region'],
  // },
  // {
  //   text: 'Airtime (hours)',
  //   value: 'airtime',
  //   icon: 'hatchet',
  //   text_zh: '直播时长(小时)',
  //   noSplit: ['language', 'country', 'region'],
  // },
];

const pr: Array<MetricsItemType> = [
  {
    text: 'News-1',
    value: 'news',
    text_zh: '新闻量',
    text_en: 'News',
    format: (value) => reviewsFormat(value, 2, '0'),
  },
  {
    text: 'Google Trends',
    value: 'google_trends',
    icon: 'google_trends',
    text_zh: 'Google 趋势',
    format: (value: number) => scoreFormat(value, 0),
    noSplit: ['language', 'country', 'region', 'channel'],
    processType: 100,
  },
];

const operation: OperationMetricsType = {
  pc: [
    {
      text: 'PCU',
      value: 'pcu',
      icon: 'steam',
      text_zh: '同时最高在线玩家',
      noSplit: ['language', 'country', 'region', 'channel'],
    },
    {
      text: 'Digital Revenue ($)',
      value: 'digital_revenue',
      icon: 'mscience_icon',
      text_zh: '线上收入(美元)',
      noSplit: ['language', 'country', 'region', 'channel'],
    },
    {
      text: 'Digital Units',
      value: 'units',
      icon: 'mscience_icon',
      text_zh: 'Steam销量',
      noSplit: ['language', 'country', 'region', 'channel'],
    },
  ],
  console: [
    {
      text: 'Digital Revenue ($)',
      value: 'digital_revenue',
      icon: 'mscience_icon',
      text_zh: '线上收入(美元)',
      noSplit: ['language', 'country', 'region', 'channel'],
    },
  ],
  mobile: [
    {
      text: 'DAU',
      value: 'dau',
      icon: 'sensor_tower',
      text_zh: '日活',
      noSplit: ['language', 'country', 'region', 'channel'],
    },
    {
      text: 'Revenue',
      value: 'revenue',
      icon: 'sensor_tower',
      text_zh: '收入',
      noSplit: ['language', 'country', 'region', 'channel'],
    },
    {
      text: 'Download',
      value: 'download',
      icon: 'sensor_tower',
      text_zh: '下载量',
      noSplit: ['language', 'country', 'region', 'channel'],
    },
  ],
};

export const metricsList: Array<MetricsType> = [
  'Opinions Overview',
  'Game Store',
  'Community',
  // 'Operation Metrics', // TODO: 展示处理隐藏Operation Metrics
  'PR',
];

export const metricsMap: {
  'Opinions Overview': Array<MetricsItemType>;
  'Game Store': GameStoreMetricsType;
  // 'Operation Metrics': OperationMetricsType; // TODO: 展示处理隐藏Operation Metrics
  Community: Array<MetricsItemType>;
  PR: Array<MetricsItemType>;
} = {
  'Opinions Overview': overview,
  'Game Store': gameStore,
  Community: community,
  PR: pr,
  // 'Operation Metrics': operation, // TODO: 展示处理隐藏Operation Metrics
};

export const metricsIconMap: { [key: string]: string } = {
  metacritic,
  opencritic,
  steam,
  xbox,
  playstation,
  app_store,
  google_play,
  hatchet,
  google_trends,
  app_annie,
  mscience_icon,
  sensor_tower,
};

export const getMetricsList = (
  type: MetricsType,
  entity_type: 'pc' | 'console' | 'mobile',
): Array<Array<MetricsItemType>> => {
  const metricsList = metricsMap[type];
  if (Array.isArray(metricsList)) {
    return [metricsList];
  }

  if (type === 'Operation Metrics') {
    return [(metricsList as OperationMetricsType)[entity_type]];
  }

  if (type === 'Game Store') {
    const result: any = {};

    for (const key in metricsList) {
      const element = (metricsList as GameStoreMetricsType)[key];

      if (element.entity_types.includes(entity_type)) {
        result[key] = element.list.map((item) => ({
          ...item,
          icon: key,
          platform: key,
        }));
      }
    }

    return Object.values(result);
  }

  return Object.values(metricsList);
};

export const getMetricsListValue = (
  type: MetricsType,
  entity_type: 'pc' | 'console' | 'mobile',
): Array<string> => {
  const list = getMetricsList(type, entity_type).flat(3);

  return list.filter((item) => !item.isSeparate).map((it) => it.value);
};

export const getAllMetricsList = (
  entity_type: 'pc' | 'console' | 'mobile',
): Array<MetricsItemType> => {
  const list: Array<MetricsItemType> = metricsList
    .map((type) => {
      return getMetricsList(type, entity_type).flat(3);
    })
    .flat(4);

  return list.filter((item) => !item.isSeparate);
};

export const getAllMetricsValue = (
  entity_type: 'pc' | 'console' | 'mobile',
): Array<string> => {
  const list: Array<MetricsItemType> = metricsList
    .map((type) => {
      return getMetricsList(type, entity_type).flat(3);
    })
    .flat(4);

  return list.filter((item) => !item.isSeparate).map((item) => item.value);
};

export const getMetricsItemFunc = (
  entity_type: 'pc' | 'console' | 'mobile',
) => {
  const list = getAllMetricsList(entity_type);

  const metricsMap = arrayToObj(list, 'value');

  return (key: string) => {
    return metricsMap[key];
  };
};
